import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';

/**
 * 342 (#933): what the host's sales statement needs from a car: the supplier's monthly cost and CARii's commission
 * rate. Its own card and its own route (`PATCH /cars/:id/sales-settings`), outside the car form's save: the general
 * car update never carries these fields. Shown only to staff who may update cars PLATFORM-WIDE; a supplier updates
 * its own cars and must not set what CARii charges it.
 */
interface SalesSettings {
  monthly_cost_minor: number | null;
  commission_rate_bps: number | null;
}

const toRinggit = (minor: number | null) => (minor === null ? '' : (minor / 100).toFixed(2));
const toPercent = (bps: number | null) => (bps === null ? '' : String(bps / 100));

export function CarSalesSettingsCard({ carId }: { carId: string }) {
  const ability = useAbility();
  const allowed = holdsAnywhere(ability as never, 'update', 'Car');
  const settings = useQuery({
    queryKey: ['car-sales-settings', carId],
    queryFn: () => api.get<SalesSettings>(`/cars/${carId}/sales-settings`),
    enabled: allowed,
  });
  const [cost, setCost] = useState('');
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!settings.data) return;
    setCost(toRinggit(settings.data.monthly_cost_minor));
    setRate(toPercent(settings.data.commission_rate_bps));
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (body: SalesSettings) =>
      api.patch<SalesSettings>(`/cars/${carId}/sales-settings`, body),
    onSuccess: () => setSaved(true),
  });

  if (!allowed) return null;

  const submit = () => {
    const c = cost.trim() === '' ? null : Math.round(Number.parseFloat(cost) * 100);
    const r = rate.trim() === '' ? null : Math.round(Number.parseFloat(rate) * 100);
    if (c !== null && (!Number.isFinite(c) || c < 0))
      return setError('The monthly cost must be 0 or more.');
    if (r !== null && (!Number.isFinite(r) || r < 0 || r > 10000))
      return setError('The rate must be between 0 and 100 %.');
    setError(null);
    setSaved(false);
    save.mutate({ monthly_cost_minor: c, commission_rate_bps: r });
  };

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Sales settings</h2>
      <p className="mb-4 text-sm text-steel">
        Used by the host's sales statement. Leave a field empty when it does not apply; it then
        counts as 0.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Supplier monthly cost (RM)" name="sales_monthly_cost">
          <Input
            id="sales_monthly_cost"
            inputMode="decimal"
            value={cost}
            onChange={(e) => {
              setCost(e.target.value);
              setSaved(false);
            }}
          />
        </FormField>
        <FormField label="CARii commission rate (%)" name="sales_commission_rate">
          <Input
            id="sales_commission_rate"
            inputMode="decimal"
            value={rate}
            onChange={(e) => {
              setRate(e.target.value);
              setSaved(false);
            }}
          />
        </FormField>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
      {save.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(save.error as { message?: string }).message ?? 'Save failed. Try again.'}
        </p>
      )}
      <div className="mt-4 flex items-center gap-3">
        <Button
          type="button"
          aria-label="Save sales settings"
          loading={save.isPending}
          onClick={submit}
        >
          Save
        </Button>
        {saved && !save.isPending && (
          <span data-testid="sales-settings-saved" className="text-sm text-green-600">
            Saved
          </span>
        )}
      </div>
    </Card>
  );
}
